import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledClickable } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@store/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    isHome = "isHome",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.isHome]: boolean;
    };
}

const mapStateToProps = (state: Toolkit.Store.ApplicationState) => {
    const location = getLocation(state);
    const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(location.search);
    const currentPage = getCurrentPage(state);
    const pagesDataView = getDataView(state.data.pages, {
        parentNodeId: currentPage.nodeId,
    });
    const pageTypesToList =
        currentPage.type === constants.pageType.blogHome ? constants.pageType.blogList : constants.pageType.blogPost;
    const pages = {
        ...pagesDataView,
        value: pagesDataView.value?.filter(x => x.type === pageTypesToList),
    };
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: getCurrentPage(state).nodeId,
        pages,
        links,
        location,
        parameter: {
            page: parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined,
            id: getCurrentPage(state).nodeId,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface BlogPageViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: GridContainerProps;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    thumbnailWrapper?: InjectableCss;
    thumbnail?: LazyImageProps;
}

export const blogPageViewStyles: BlogPageViewStyles = {
    titleText: {
        as: "h2",
        css: css`
            font-size: ${x => x.theme.design.typography.size.larger};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin: 0;
            text-transform: uppercase;
            color: ${getColor("primary.dark")};
            ${StyledClickable} {
                color: inherit;
            }
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${breakpoint("sm", "up")} {
                margin-left: -${pxToRem(15)};
                margin-right: -${pxToRem(15)};
            }
        `,
    },
    newsWrapper: {
        css: css`
            padding: ${pxToRem(0, 15, 40, 15)};
            ${breakpoint("xs", "down")} {
                padding: ${pxToRem(0, 0, 40, 0)};
            }
        `,
    },
    authorText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    publishDateText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.base};
            display: block;
            margin-top: ${pxToRem(10)};
            color: ${getColor("primary.light")};
        `,
    },
    thumbnailWrapper: {
        css: css`
            a {
                display: block;
            }
        `,
    },
    thumbnail: {
        css: css`
            width: 100%;
            height: auto;
            position: relative;
            overflow: hidden;
            padding: 58% 0 0 0;

            img {
                object-fit: cover;
                display: block;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
            }
        `,
    },
};

const styles = blogPageViewStyles;

const BlogListView: FC<Props> = ({ links, pages, fields, location, parameter, history, loadPagesForParent }) => {
    const { defaultPageSize, isHome } = fields;
    const [pageSize, setPageSize] = useState(defaultPageSize);

    if (!pages.value && !pages.isLoading) {
        loadPagesForParent({
            page: parameter.page,
            pageSize: defaultPageSize,
            parentNodeId: parameter.id,
        });
    }

    const changePage = (newPageIndex: number) => {
        updateQueryString(newPageIndex, defaultPageSize);
    };

    const updateQueryString = (page: number, pageSize?: number) => {
        let newLocation = `${location.pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    };

    const totalItems = pages.value?.length || 0;
    const page = parameter.page ?? 1;

    const sortedAndPagedPages = sortBy(pages.value, o => o.fields["publishDate"])
        .reverse()
        .slice((page - 1) * pageSize, page * pageSize);

    const colWidth = isHome
        ? ["0 0 100%", "0 0 50%", "0 0 33.33%", "0 0 25%", "0 0 25%"]
        : ["0 0 100%", "0 0 100%", "0 0 50%", "0 0 33.33%", "0 0 33.33%"];

    const gridWrapper: GridItemProps = {
        as: "li",
        css: css`
            ${styles.newsWrapper?.css}
            ${breakpointValues(colWidth, "flex")}
        `,
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        setPageSize(newPageSize);
    };

    return (
        <>
            <StyledWrapper {...styles.listWrapper} as="ul">
                {sortedAndPagedPages.map((page: PageProps) => {
                    const thumbnail = page.fields["categoryImage"] || page.fields["newsImage"];
                    const thumbnailAlt = page.fields["categoryImageAlt"] || page.fields["newsImageAlt"];
                    return (
                        <StyledWrapper {...gridWrapper} key={page.id}>
                            <StyledWrapper {...styles.thumbnailWrapper}>
                                <Link href={links[page.nodeId]?.url}>
                                    <LazyImage {...styles.thumbnail} src={thumbnail} altText={thumbnailAlt} />
                                </Link>
                            </StyledWrapper>
                            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                {page.fields["author"] && (
                                    <Typography {...styles.authorText}>{page.fields["author"]}</Typography>
                                )}
                                {page.fields["publishDate"] && (
                                    <Typography {...styles.publishDateText}>
                                        <LocalizedDateTime
                                            dateTime={new Date(page.fields["publishDate"])}
                                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                        />
                                    </Typography>
                                )}
                            </StyledWrapper>
                            <Typography {...styles.titleText}>
                                <Link href={links[page.nodeId]?.url}>{page.fields["title"]}</Link>
                            </Typography>
                            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                <StyledWrapper {...styles.summaryWrapper}>
                                    {parse(
                                        page.fields["newsSummary"] ||
                                            truncate(page.fields["newsContent"], { length: 500 }),
                                        parserOptions,
                                    )}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
            {totalItems > defaultPageSize && (
                <Pagination
                    resultsCount={totalItems}
                    currentPage={page}
                    resultsPerPage={pageSize}
                    resultsPerPageOptions={[
                        defaultPageSize,
                        defaultPageSize * 2,
                        defaultPageSize * 3,
                        defaultPageSize * 4,
                    ]}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                    {...styles.pagination}
                />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(BlogListView)),
    definition: {
        group: constants.widgetGroup.blogList,
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 12,
                fieldType: "General",
                min: 1,
                max: 20,
            },
            {
                name: fields.isHome,
                displayName: "Blog Root",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tooltip:
                    "Please check this option if this list is used on the Blog's Home Page to display its categories instead of articles.",
            },
        ],
    },
};

export default widgetModule;
